import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type PoliciesGlobalSortOption =
  | 'created-desc'
  | 'created-asc'
  | 'last-run-desc'
  | 'rollouts-desc'
  | 'autonomy-desc'
  | 'success-rate-desc';

export const DEFAULT_POLICIES_GLOBAL_SORT: PoliciesGlobalSortOption = 'created-desc';

const STORAGE_KEY = 'bastion.policies.display';

/** Version 1 has the sort options of the policy catalog list. */
const STORAGE_VERSION = 1;

/**
 * Version 0 sort options and their version 1 values. `success-desc` was
 * "Highest full-auto %", which measures operator time, so it becomes
 * "Highest autonomy".
 */
const VERSION_0_SORTS: Record<string, PoliciesGlobalSortOption> = {
  'date-desc': 'created-desc',
  'date-asc': 'created-asc',
  'rollouts-desc': 'rollouts-desc',
  'success-desc': 'autonomy-desc',
  'auto-desc': 'autonomy-desc',
};

export interface PoliciesDisplayState {
  globalSort: PoliciesGlobalSortOption;
  setGlobalSort: (sort: PoliciesGlobalSortOption) => void;
  reset: () => void;
}

type PersistedPoliciesDisplay = Pick<PoliciesDisplayState, 'globalSort'>;

function migratePoliciesDisplay(persisted: unknown, version: number): PersistedPoliciesDisplay {
  const stored =
    persisted && typeof persisted === 'object'
      ? (persisted as { globalSort?: unknown }).globalSort
      : undefined;
  const globalSort =
    version === 0 && typeof stored === 'string' ? VERSION_0_SORTS[stored] : undefined;
  return { globalSort: globalSort ?? DEFAULT_POLICIES_GLOBAL_SORT };
}

export const usePoliciesDisplayStore = create<PoliciesDisplayState>()(
  persist(
    (set) => ({
      globalSort: DEFAULT_POLICIES_GLOBAL_SORT,
      setGlobalSort: (globalSort) => set({ globalSort }),
      reset: () => set({ globalSort: DEFAULT_POLICIES_GLOBAL_SORT }),
    }),
    {
      name: STORAGE_KEY,
      version: STORAGE_VERSION,
      migrate: migratePoliciesDisplay,
      partialize: (state): PersistedPoliciesDisplay => ({ globalSort: state.globalSort }),
    },
  ),
);
